import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUserImageLimits } from '../api/api';
import type { UserImageLimits } from '@UserImageLimits';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';

const UpgradePrompt: React.FC = () => {
    const [limits, setLimits] = useState<UserImageLimits | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    useEffect(() => {
        loadLimits();
    }, []);

    const loadLimits = async () => {
        try {
            setLoading(true);
            const userLimits = await getUserImageLimits();
            setLimits(userLimits);
        } catch (error) {
            console.error('Error loading image limits:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpgradeClick = () => {
        navigate('/account-settings');
    };

    // Don't show anything if loading or if limits are not reached
    if (loading || !limits || !limits.limitReached || limits.tier === 'unlimited') {
        return null;
    }

    const buttonContent = isMobile ? (
        <>⭐ {t('account.upgradeButtonShort')}</>
    ) : (
        <>⭐ {t('account.upgradePrompt')}</>
    );

    return (
        <div className="flex items-center">
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="default"
                            size="sm"
                            onClick={handleUpgradeClick}
                            className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs sm:text-sm h-7 sm:h-6 px-2 sm:px-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap relative overflow-hidden group"
                        >
                            <span className="relative z-10">{buttonContent}</span>
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                        </Button>
                    </TooltipTrigger>
                    {isMobile && (
                        <TooltipContent>
                            <p>{t('account.upgradePrompt')}</p>
                        </TooltipContent>
                    )}
                </Tooltip>
            </TooltipProvider>
        </div>
    );
};

export default UpgradePrompt;
